import React from 'react';
import CloudscapeErrorBoundary from '@amzn/awsui-components-core/error-boundary';
import Link from '@amzn/awsui-components-core/link';
import { TICKET_LINKS } from 'src/constants/appConstants';
import { logger } from 'src/utils/logger';
import { trackError } from 'src/utils/metrics';

/** SessionStorage key to prevent infinite reload loops on persistent chunk failures */
const CHUNK_RELOAD_KEY = 'verity-chunk-reload';

/**
 * Detects webpack ChunkLoadError (failed lazy-loaded JS/CSS chunks).
 * This typically happens after a deployment when CloudFront serves stale or missing chunks.
 */
const isChunkLoadError = (error: Error): boolean =>
  error.name === 'ChunkLoadError' ||
  error.message?.includes('Loading chunk') ||
  error.message?.includes('Loading CSS chunk');

/**
 * Props for AppErrorBoundary component
 */
interface AppErrorBoundaryProps {
  /** Child components to wrap */
  children: React.ReactNode;
}

/**
 * AppErrorBoundary - Uses the native Cloudscape ErrorBoundary component
 *
 * Catches JavaScript errors in child component tree and displays a
 * Cloudscape-styled error message with a link to file a Verity support ticket.
 *
 * For ChunkLoadError (stale/missing webpack chunks after deployment), automatically
 * reloads the page once to pick up the latest assets. Uses sessionStorage to prevent
 * infinite reload loops.
 *
 * Reference: https://core.cloudscape.aws.dev/components/error-boundary/
 *
 * @example
 * ```tsx
 * <AppErrorBoundary>
 *   <RpnContainer />
 * </AppErrorBoundary>
 * ```
 */
export const AppErrorBoundary: React.FC<AppErrorBoundaryProps> = ({ children }) => {
  return (
    <CloudscapeErrorBoundary
      i18nStrings={{
        components: {
          Feedback: ({ children: feedbackChildren }) => (
            <Link href={TICKET_LINKS.VERITY_SUPPORT.href} external>
              {feedbackChildren}
            </Link>
          ),
        },
      }}
      onError={({ error }) => {
        // Auto-reload on ChunkLoadError (once per session to avoid infinite loops)
        if (isChunkLoadError(error)) {
          try {
            const alreadyReloaded = sessionStorage.getItem(CHUNK_RELOAD_KEY);
            if (!alreadyReloaded) {
              logger.warn('AppErrorBoundary: ChunkLoadError detected, reloading page', {
                error: error?.message,
              });
              sessionStorage.setItem(CHUNK_RELOAD_KEY, Date.now().toString());
              window.location.reload();
              return;
            }
            // Already reloaded once this session — show error UI
            logger.error('AppErrorBoundary: ChunkLoadError persists after reload', {
              error: error?.message,
              stack: error?.stack,
            });
          } catch {
            // sessionStorage unavailable — log accurately and fall through to error UI
            logger.error('AppErrorBoundary: ChunkLoadError detected, sessionStorage unavailable', {
              error: error?.message,
              stack: error?.stack,
            });
          }
        } else {
          logger.error('AppErrorBoundary caught an error', {
            error: error?.message,
            stack: error?.stack,
          });
        }

        // Track error in metrics — sanitize message for CloudWatch dimension compatibility
        const sanitizedMessage = (error?.message || 'Unknown error').replace(/[\n\r]/g, ' ').slice(0, 200);
        trackError('UnhandledError', {
          errorMessage: sanitizedMessage,
          errorName: error?.name || 'Error',
        });
      }}
    >
      {children}
    </CloudscapeErrorBoundary>
  );
};

export default AppErrorBoundary;
